/**
 * Help Document Service - Fetch and store help documents for offline use
 *
 * Help documents are HTML content that provide contextual help for document fields.
 * They are fetched from the API and stored in IndexedDB for offline access.
 */

import db from '@/db';
import { HelpDocument } from '../types/help_document';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { fetchWithCache } from '../../lib/api-client';

import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('HelpDocument', LogLevel.DEBUG);
// Constants
const FETCH_TIMEOUT_MS = 30000;

/**
 * Fetch a help document by slug from the API and store in IndexedDB
 */
export async function fetchAndStoreHelpDocument(slug: string): Promise<HelpDocument | null> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('[HelpDocumentService] No current person - skipping fetch');
    return null;
  }

  // Include current_person_id for authentication (consistent with other services)
  const url = `${API_ENDPOINTS.help}/${slug}?current_person_id=${currentPerson.id}`;

  try {
    const { response, notModified, commit } = await fetchWithCache(url, {
      organizationId: currentPerson.organization.id,
      name: `help_document_${slug}`,
    }, {
      timeout: FETCH_TIMEOUT_MS,
      haveBody: async () => (await db.helpDocuments.where('slug').equals(slug).count()) > 0,
    });

    if (notModified) {
      logger.debug(`[HelpDocumentService] Help document not modified (304): ${slug}`);
      return await db.helpDocuments.where('slug').equals(slug).first() ?? null;
    }

    if (!response.ok) {
      if (response.status === 404) {
        logger.debug(`[HelpDocumentService] Help document not found: ${slug}`);
        return null;
      }
      throw new Error(`Failed to fetch help document: ${response.status}`);
    }

    const data = await response.json();

    const helpDocument: HelpDocument = data;

    // Store in IndexedDB
    await db.helpDocuments.put(helpDocument);
    await commit();

    logger.debug(`[HelpDocumentService] Stored help document: ${slug}`);
    return helpDocument;
  } catch (error) {
    logger.error(`[HelpDocumentService] Error fetching help document ${slug}:`, error);
    return null;
  }
}

/**
 * Get a help document from local DB by slug
 */
export async function getHelpDocument(slug: string): Promise<HelpDocument | undefined> {
  return db.helpDocuments.where('slug').equals(slug).first();
}

/**
 * Refresh help documents for all templates that have help.slug defined
 * This should be called after templates are refreshed
 */
export async function refreshHelpDocuments(): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('[HelpDocumentService] No current person - skipping refresh');
    return;
  }

  // Get all templates for the current organization
  const templates = await db.documentTemplates
    .where('organization_id')
    .equals(currentPerson.organization.id)
    .toArray();

  // Extract unique help slugs from templates — from definition.help.slug
  // (form-level help) and summary.help.slug (summary-tab CAP manual).
  const helpSlugs = new Set<string>();

  for (const template of templates) {
    const helpSlug = template.super_form.definition.help?.slug;
    if (helpSlug) {
      helpSlugs.add(helpSlug);
    }

    const summaryHelpSlug = template.super_form.definition.summary?.help?.slug;
    if (summaryHelpSlug) {
      helpSlugs.add(summaryHelpSlug);
    }
  }

  if (helpSlugs.size === 0) {
    logger.debug('[HelpDocumentService] No templates with help documents found');
    return;
  }

  logger.debug(`[HelpDocumentService] Refreshing ${helpSlugs.size} help document(s)`);

  // Fetch each help document - use allSettled to continue even if some fail
  const fetchPromises = Array.from(helpSlugs).map(slug => fetchAndStoreHelpDocument(slug));
  const results = await Promise.allSettled(fetchPromises);

  // Log any failures
  const failures = results.filter(r => r.status === 'rejected');
  if (failures.length > 0) {
    logger.warn(`[HelpDocumentService] ${failures.length} help document(s) failed to fetch`);
  }

  const successes = results.filter(r => r.status === 'fulfilled' && r.value !== null);
  logger.debug(`[HelpDocumentService] Help documents refresh complete: ${successes.length}/${helpSlugs.size} succeeded`);
}
